import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { createInitialGameState, createLightingSandboxState } from '~/game/engine/state'
import type { ClockMode } from '~/game/engine/types'
import { GameDevtools } from '~/game/devtools/GameDevtools'
import { useGameBrowserBridge } from '~/game/ui/useGameBrowserBridge'
import { GameScreen } from '~/game/ui/GameScreen'
import { useGameClock } from '~/game/ui/useGameClock'
import { useGameController } from '~/game/ui/useGameController'
import { usePickupToasts } from '~/game/ui/usePickupToasts'

export const Route = createFileRoute('/game')({
  validateSearch: (search: Record<string, unknown>) => ({
    clock: (search.clock === 'manual' ? 'manual' : 'auto') as ClockMode,
    sandbox: search.sandbox === 'lighting' ? ('lighting' as const) : undefined,
  }),
  component: GameRoute,
  // ssr: false,
})

function GameRoute() {
  const { clock: clockMode, sandbox } = Route.useSearch()
  const navigate = Route.useNavigate()
  const [isCraftingOpen, setIsCraftingOpen] = useState(false)
  const [isInventoryOpen, setIsInventoryOpen] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const activeSandbox = sandbox ?? 'default'
  const createInitialState =
    activeSandbox === 'lighting' ? createLightingSandboxState : createInitialGameState
  const { store, advanceBy, handleCellPress, resetGame } = useGameController(createInitialState)
  usePickupToasts(store)
  const showDevClockControls = import.meta.env.DEV

  const handleRestart = () => {
    setIsCraftingOpen(false)
    setIsInventoryOpen(false)
    setIsPaused(false)
    resetGame()
  }

  const handleResume = () => {
    setIsPaused(false)
  }

  useGameClock(advanceBy, clockMode !== 'manual' && !isPaused)
  useGameBrowserBridge({
    advanceBy,
    clockMode,
    store,
  })

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return
      }

      if (
        event.key === 'Escape' &&
        !event.altKey &&
        !event.ctrlKey &&
        !event.metaKey &&
        !event.shiftKey
      ) {
        event.preventDefault()
        if (isInventoryOpen || isCraftingOpen) {
          setIsInventoryOpen(false)
          if (isCraftingOpen) {
            store.actions.flushInventoryCrafting()
          }
          setIsCraftingOpen(false)
          return
        }

        setIsPaused((current) => !current)
        return
      }

      if (
        event.key !== 'Tab' ||
        event.altKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey
      ) {
        return
      }

      event.preventDefault()
      setIsInventoryOpen((current) => !current)
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isCraftingOpen, isInventoryOpen, store])

  return (
    <main className="game-route-shell">
      <GameScreen
        isCraftingOpen={isCraftingOpen}
        hasDevtools={showDevClockControls}
        isInventoryOpen={isInventoryOpen}
        isPaused={isPaused}
        onCellPress={handleCellPress}
        onResume={handleResume}
        onToggleCrafting={() => {
          setIsCraftingOpen((current) => {
            if (current) {
              store.actions.flushInventoryCrafting()
            }
            return !current
          })
        }}
        onToggleInventory={() => setIsInventoryOpen((current) => !current)}
        onRestart={handleRestart}
        store={store}
      />
      {showDevClockControls ? (
        <div className="game-devtools-overlay">
          <GameDevtools
            clockMode={clockMode}
            onSetClockMode={(nextClockMode) => {
              void navigate({
                search: {
                  clock: nextClockMode,
                  sandbox,
                },
              })
            }}
            onStepTime={advanceBy}
            store={store}
          />
        </div>
      ) : null}
    </main>
  )
}
